import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import PublicNav from '@/components/PublicNav';
import PublicFooter from '@/components/PublicFooter';
import { useMultilingualSEO } from '@/hooks/useMultilingualSEO';

const API = '';

export default function ChapterDetail() {
  const { id, lang: urlLang } = useParams();
  const { t, i18n } = useTranslation();
  const [chapter, setChapter] = useState(null);
  const [loading, setLoading] = useState(true);
  const { getCanonicalUrl, generateHreflangTags, currentLangConfig, SITE_URL } = useMultilingualSEO('chapter');

  useEffect(() => {
    if (urlLang && ['ta', 'te', 'bn', 'kn', 'or', 'as', 'gu', 'ml', 'es', 'en', 'hi'].includes(urlLang)) {
      i18n.changeLanguage(urlLang);
    }
  }, [urlLang, i18n]);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/public/chapters/${id}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { setChapter(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-10 h-10 border-3 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (!chapter) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-semibold mb-2">Chapter not found</h1>
          <Link to="/chapters" className="text-primary hover:underline">Browse all chapters</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="chapter-detail-page">
      <Helmet>
        <title>{t('seo.chapter_title', { chapter: chapter.chapter, name: chapter.name_en })}</title>
        <meta name="description" content={t('seo.chapter_desc', { chapter: chapter.chapter, verses: chapter.verse_count })} />
        <link rel="canonical" href={getCanonicalUrl(`/chapter/${chapter.chapter}`)} />
        {generateHreflangTags(`/chapter/${chapter.chapter}`)}
        
        <meta property="og:title" content={t('seo.chapter_title', { chapter: chapter.chapter, name: chapter.name_en })} />
        <meta property="og:description" content={t('seo.chapter_desc', { chapter: chapter.chapter, verses: chapter.verse_count })} />
        <meta property="og:url" content={getCanonicalUrl(`/chapter/${chapter.chapter}`)} />
        <meta property="og:type" content="article" />
        <meta property="og:locale" content={currentLangConfig.locale} />
        <meta property="og:image" content={`${SITE_URL}/og-logo.png`} />
        
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Chapter",
          "name": `Bhagavad Gita ${t('common.chapter')} ${chapter.chapter}: ${chapter.name_en}`,
          "description": t('seo.chapter_desc', { chapter: chapter.chapter, name: chapter.name_en, verses: chapter.verse_count }),
          "inLanguage": currentLangConfig.hreflang,
          "url": getCanonicalUrl(`/chapter/${chapter.chapter}`),
          "isPartOf": { "@type": "Book", "name": "Bhagavad Gita", "alternateName": ["Bhagwad Geeta", "Bhagwat Gita", "भगवद गीता"] }
        })}</script>
      </Helmet>

      {/* Nav */}
      <PublicNav />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 md:px-12 py-10 md:py-14">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-8">
          <Link to="/chapters" className="hover:text-foreground transition-colors">Bhagavad Gita</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-foreground">Chapter {chapter.chapter}</span>
        </div>

        {/* Chapter Header */}
        <div className="mb-10">
          <p className="text-xs uppercase tracking-[0.2em] text-primary/60 mb-2 font-medium">Chapter {chapter.chapter}</p>
          <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold tracking-tight text-foreground mb-2">
            {chapter.name_en}
          </h1>
          <p className="font-['Noto_Sans_Devanagari'] text-lg text-muted-foreground mb-3">{chapter.name_hi}</p>
          <p className="text-sm text-muted-foreground">{chapter.verse_count} {t('verse.verses')}</p>
        </div>

        {/* Verse List */}
        <div className="space-y-2" data-testid="verse-list">
          {chapter.verses.map(v => (
            <Link
              key={v.verse_id}
              to={`${urlLang ? `/${urlLang}` : ''}/verse/${chapter.chapter}/${v.verse_number}`}
              className="group flex items-start gap-4 p-4 rounded-xl hover:bg-primary/[0.03] border border-transparent hover:border-border/30 transition-all"
              data-testid={`verse-link-${v.verse_number}`}
            >
              <span className="text-sm font-medium text-primary/40 group-hover:text-primary transition-colors min-w-[2rem] pt-0.5">
                {chapter.chapter}.{v.verse_number}
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-['Noto_Sans_Devanagari'] text-sm text-foreground/70 leading-relaxed truncate mb-0.5">
                  {v.sanskrit?.split('\n')[0]}
                </p>
                <p className="text-sm text-muted-foreground truncate">{v.translation_en}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground/30 group-hover:text-primary shrink-0 mt-1 transition-colors" />
            </Link>
          ))}
        </div>

        {/* Chapter Navigation */}
        <div className="flex items-center justify-between mt-12 pt-8 border-t border-border/30">
          {chapter.prev_chapter ? (
            <Link to={`${urlLang ? `/${urlLang}` : ''}/chapter/${chapter.prev_chapter}`} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="prev-chapter-link">
              <ChevronLeft className="w-4 h-4" />
              <span>{t('verse.chapter_title')} {chapter.prev_chapter}</span>
            </Link>
          ) : <div />}
          <Link to={`${urlLang ? `/${urlLang}` : ''}/chapters`} className="text-sm text-primary hover:underline">{t('verse.all_chapters')}</Link>
          {chapter.next_chapter ? (
            <Link to={`${urlLang ? `/${urlLang}` : ''}/chapter/${chapter.next_chapter}`} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="next-chapter-link">
              <span>{t('verse.chapter_title')} {chapter.next_chapter}</span>
              <ChevronRight className="w-4 h-4" />
            </Link>
          ) : <div />}
        </div>
      </main>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
}
